import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const interceptorRef = useRef(null);

  // Memoized logout so it's stable across renders
  const logout = useCallback(() => {
    localStorage.removeItem("token");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
  }, []);

  // Fetch current user from /auth/me
  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error("Error fetching user:", error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  // Register axios 401 interceptor – automatically logs out when token expires
  useEffect(() => {
    interceptorRef.current = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error?.response?.status === 401) {
          // Skip the /auth/login endpoint to avoid self-loop
          const isLoginRequest = error.config?.url?.includes("/auth/login");
          if (!isLoginRequest) {
            logout();
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      if (interceptorRef.current !== null) {
        axios.interceptors.response.eject(interceptorRef.current);
      }
    };
  }, [logout]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = async (email, password, twoFactorCode = null) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { 
      email, 
      password,
      two_factor_code: twoFactorCode 
    });
    
    // Si requiere 2FA, retornar sin establecer el usuario
    if (response.data.requires_two_factor) {
      return { requires_two_factor: true };
    }
    
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const setup2FA = async () => {
    const response = await axios.post(`${API_URL}/api/auth/2fa/setup`);
    return response.data;
  };

  const enable2FA = async (code) => {
    const response = await axios.post(`${API_URL}/api/auth/2fa/enable`, { code });
    return response.data;
  };

  const disable2FA = async (code) => {
    const response = await axios.post(`${API_URL}/api/auth/2fa/disable`, { code });
    return response.data;
  };

  const isAdmin = user?.role === "admin";

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      logout, 
      isAdmin, 
      setup2FA, 
      enable2FA, 
      disable2FA 
    }}>
      {children}
    </AuthContext.Provider>
  );
};
